import { useId, type HTMLAttributes } from 'react';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-reactjs';
import { useTimeZone } from '@/lib/time';
import { cx } from '../internal/cx';
import { getPageTokens } from './paginationUtils';
import styles from './Pagination.module.css';

export interface PaginationProps extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  /** Current page, 1-based. */
  page: number;
  pageSize: number;
  /** Total number of items across all pages. */
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Pages shown either side of the current page. */
  siblingCount?: number;
  /** Accessible name of the navigation landmark. */
  label?: string;
  disabled?: boolean;
}

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  siblingCount = 1,
  label = 'Pagination',
  disabled = false,
  className,
  ...rest
}: PaginationProps) {
  const { locale } = useTimeZone();
  const sizeId = useId();
  const numberFormat = new Intl.NumberFormat(locale);
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const end = Math.min(total, current * pageSize);
  const tokens = getPageTokens(current, totalPages, siblingCount);

  const summary =
    total === 0
      ? 'No results'
      : `Showing ${numberFormat.format(start)}–${numberFormat.format(end)} of ${numberFormat.format(total)}`;

  function go(target: number) {
    if (disabled) return;
    const clamped = Math.min(Math.max(1, target), totalPages);
    if (clamped !== current) onPageChange(clamped);
  }

  return (
    <nav aria-label={label} className={cx(styles.root, className)} {...rest}>
      <p className={styles.summary} aria-live="polite">
        {summary}
      </p>
      <div className={styles.controls}>
        {onPageSizeChange && (
          <div className={styles.pageSize}>
            <label htmlFor={sizeId} className={styles.pageSizeLabel}>
              Rows per page
            </label>
            <select
              id={sizeId}
              className={styles.select}
              value={pageSize}
              disabled={disabled}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {(pageSizeOptions.includes(pageSize)
                ? pageSizeOptions
                : [...pageSizeOptions, pageSize].sort((a, b) => a - b)
              ).map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
        <ul className={styles.pages}>
          <li>
            <button
              type="button"
              className={cx(styles.pageButton, styles.stepButton)}
              aria-label="Previous page"
              disabled={disabled || current <= 1}
              onClick={() => go(current - 1)}
            >
              <ArrowLeft2 size={16} color="currentColor" aria-hidden="true" />
            </button>
          </li>
          {tokens.map((token) =>
            typeof token === 'number' ? (
              <li key={token} className={styles.pageNumberItem}>
                <button
                  type="button"
                  className={cx(styles.pageButton, token === current && styles.current)}
                  aria-label={`Page ${token}`}
                  aria-current={token === current ? 'page' : undefined}
                  disabled={disabled}
                  onClick={() => go(token)}
                >
                  {numberFormat.format(token)}
                </button>
              </li>
            ) : (
              <li
                key={token}
                className={cx(styles.ellipsis, styles.pageNumberItem)}
                aria-hidden="true"
              >
                …
              </li>
            ),
          )}
          <li className={styles.compactStatus} aria-hidden="true">
            {numberFormat.format(current)} / {numberFormat.format(totalPages)}
          </li>
          <li>
            <button
              type="button"
              className={cx(styles.pageButton, styles.stepButton)}
              aria-label="Next page"
              disabled={disabled || current >= totalPages}
              onClick={() => go(current + 1)}
            >
              <ArrowRight2 size={16} color="currentColor" aria-hidden="true" />
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}
